import styled, { css } from 'styled-components'
import { ReactComponent as MagGlassSvg } from '@sketch/icons/img-magnifying-glass-17'
import { breakpoint } from '@sketch/global-styles'
import { ButtonUnstyled } from '../Button'
import { Input } from '../Input'

export const Wrapper = styled.div<{ $expanded?: boolean }>(
  ({ $expanded, theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    width: ${$expanded ? '100%' : '40px'};
    overflow: hidden;

    transition: width
      ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};

    /* For mobile views we want the search to appear first */
    ${breakpoint('base', 'sm')`
      flex-direction: row-reverse;
      align-items: center;
    `}
  `
)

export const iconStyles = css`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground.secondary.B};

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.foreground.secondary.A};
  }

  &:active {
    color: ${({ theme }) => theme.colors.foreground.secondary.B};
  }
`

export const LoupeIconContainer = styled(ButtonUnstyled)<{
  $collapsed?: boolean
}>(
  ({ $collapsed, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;

    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;
    margin: auto;

    transition: all
      ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};

    ${$collapsed
      ? css`
          left: 0;
          width: 40px;
          height: 32px;
          border-radius: ${theme.radii.large};

          &:hover {
            background-color: ${theme.colors.state.hover};
          }
        `
      : css`
          left: 8px;
          width: 24px;
          height: 24px;
        `}
  `
)

export const LoupeIcon = styled(MagGlassSvg).attrs({
  role: 'img',
  'aria-label': 'Search',
})(
  ({ theme }) => css`
    width: 17px;
    height: 17px;

    transition: all
      ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};

    ${iconStyles};

    color: ${({ theme }) => theme.colors.foreground.secondary.D};
  `
)

export const SearchInput = styled(Input)<{
  $hidden?: boolean
  $hasContent: boolean
}>(
  ({ theme, $hasContent }) => css`
    height: 32px;

    /* &[type='search'] is needed because inputs already styles that selector,
    so we need to overwrite them */
    &[type='search'] {
      font-size: ${theme.fontSizes.D};
      border-radius: ${theme.radii.large};
      padding-left: 36px;
      padding-right: ${$hasContent ? '40px' : '0px'};
      background: ${theme.colors.background.tertiary.C};

      ::-webkit-search-decoration,
      ::-webkit-search-cancel-button {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-appearance: none;
        appearance: none;
      }

      &::placeholder {
        color: ${theme.colors.state.disabled};
      }

      &:disabled {
        border: 1px dashed ${theme.colors.border.A};
        background: ${theme.colors.background.secondary.B};
      }

      &:focus {
        background-color: ${({ theme }) => theme.colors.background.secondary.A};
      }

      opacity: 1;
      width: 100%;
    }
  `
)

export const Form = styled.form<{ width?: number }>(
  ({ theme, width }) => css`
    width: 100%;
    border-radius: ${theme.radii.large};

    transition: all
      ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};

    ${typeof width !== 'undefined' &&
    breakpoint('sm')`
      width: ${width}px;
    `};
  `
)

export const ClearButtonAndChildrenWrapper = styled.div`
  position: absolute;
  right: 4px; /* stylelint-disable-line scales/space */
  top: 0;
  bottom: 0;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100%;
`

export const FormContent = styled.div<{ $fullWidth?: boolean }>(
  ({ $fullWidth }) => css`
    position: relative;
    max-width: ${$fullWidth ? '100%' : '280px'};
    height: 32px;

    ${breakpoint('sm')`
      width: ${$fullWidth ? '100%' : '280px'};
    `}
  `
)
